import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ArrowRight, ExternalLink, Loader2, Check } from "lucide-react";
import { api } from "@/lib/api";

const FULL_PAYMENT_URL = "https://square.link/u/DV0t6n3H";

// Fire an analytics event if either GA4 or Meta Pixel has already been
// initialised by the CookieConsent loader. If neither is present, do nothing.
function trackEvent(name, meta) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", name, meta || {});
    }
  } catch { /* ignore */ }
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", name, meta || {});
    }
  } catch { /* ignore */ }
}

export default function ApplicationReceived() {
  const location = useLocation();
  const applicationId = useMemo(() => {
    const fromState = location.state && location.state.applicationId;
    if (fromState) return String(fromState);
    try { return sessionStorage.getItem("bcu_last_application_id") || ""; } catch { return ""; }
  }, [location.state]);

  const [financingBusy, setFinancingBusy] = useState(false);
  const [financingJoined, setFinancingJoined] = useState(false);
  const [financingError, setFinancingError] = useState("");

  useEffect(() => {
    if (!applicationId) return;
    try {
      const key = `bcu_financing_joined:${applicationId}`;
      if (sessionStorage.getItem(key) === "1") setFinancingJoined(true);
    } catch { /* ignore */ }
  }, [applicationId]);

  const steps = [
    { n: "01", t: "Application Review", d: "Our team reviews the information you submitted.", c: "text-[#F7931E]" },
    { n: "02", t: "Enrollment Conversation", d: "Qualified applicants may be invited to speak with the enrollment team.", c: "text-[#168CFF]" },
    { n: "03", t: "Enrollment Decision", d: "Approved applicants receive the mentorship agreement and approved payment instructions.", c: "text-[#78C800]" },
  ];

  // Best-effort preference tracking before opening the external Square link.
  // The link opens regardless of tracking outcome (see click handler).
  const recordPreference = async (pref) => {
    if (!applicationId) return;
    try { await api.post(`/applications/${applicationId}/payment-preference`, { payment_preference: pref }); }
    catch { /* silent — never block payment */ }
  };

  const onFullPayClick = () => {
    trackEvent("bcu_payment_full_click", applicationId ? { application_id: applicationId } : {});
    // Fire-and-forget preference tracking; do not await so Safari's popup
    // heuristics do not treat the click as programmatic and block the tab.
    recordPreference("pay_in_full");
  };
  const onDepositClick = () => {
    trackEvent("bcu_deposit_click", applicationId ? { application_id: applicationId } : {});
    recordPreference("deposit");
  };

  const joinFinancingWaitlist = async () => {
    if (financingBusy || financingJoined) return;
    setFinancingError("");
    if (!applicationId) {
      setFinancingError("We could not locate your application on this device. Please contact the enrollment team.");
      return;
    }
    setFinancingBusy(true);
    try {
      await api.post(`/applications/${applicationId}/payment-preference`, { payment_preference: "financing_waitlist" });
      setFinancingJoined(true);
      try { sessionStorage.setItem(`bcu_financing_joined:${applicationId}`, "1"); } catch { /* ignore */ }
      trackEvent("bcu_financing_waitlist_joined", { application_id: applicationId });
    } catch {
      setFinancingError("We couldn't save your selection. Please try again in a moment.");
    } finally {
      setFinancingBusy(false);
    }
  };

  return (
    <div data-testid="app-received-page" className="sect-navy pt-32 pb-24 min-h-screen">
      <div className="bcu-container max-w-4xl">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#78C800]/40 bg-[#78C800]/10 text-[#78C800] text-[11px] tracking-[0.22em] uppercase rounded font-bold">
          <CheckCircle2 className="h-4 w-4" /> Application Received
        </div>
        <h1 className="mt-6 text-4xl md:text-6xl lg:text-7xl font-display leading-[1.02]">Your application has been <span className="text-[#F7931E]">successfully submitted.</span></h1>
        <div className="mt-6 max-w-2xl space-y-4 text-[#C7CFDD] text-[17px] leading-relaxed">
          <p>Our enrollment team will review your goals, current business stage, readiness and overall program fit.</p>
          <p>If your application appears to be a strong fit, a member of the team will contact you regarding the next step.</p>
          <p className="text-[#858585] text-[15px]">Submitting an application does not by itself confirm enrollment. A seat is secured only after the applicable payment has been successfully completed and verified, subject to availability.</p>
        </div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {steps.map((s) => (
            <div key={s.n} className="bcu-card p-6">
              <div className={`font-display text-3xl ${s.c}`}>{s.n}</div>
              <div className="mt-3 font-display text-xl">{s.t}</div>
              <p className="mt-2 text-sm text-[#C7CFDD]">{s.d}</p>
            </div>
          ))}
        </div>

        {/* --- Choose How You Would Like to Secure Your Seat --- */}
        <section data-testid="secure-seat-section" className="mt-20">
          <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Next Step</div>
          <h2 className="mt-3 font-display text-3xl md:text-5xl leading-[1.05]">Choose How You Would Like to Secure Your Seat</h2>
          <p className="mt-5 max-w-2xl text-[#C7CFDD] text-[16px] leading-relaxed">
            Bounce City University is accepting a maximum of 15 students for the upcoming 90-Day Program beginning August 12, 2026. After submitting your application, choose the option that best matches your current situation.
          </p>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {/* Option 1 — Pay in Full (current limited-time $5,000 offer) */}
            <div data-testid="secure-option-full" className="bcu-card p-7 flex flex-col">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-bold">Option 01 · Limited-Time Offer</div>
              <h3 className="mt-3 font-display text-2xl leading-tight">Pay in Full and Secure Your Enrollment</h3>
              <div className="mt-4 text-[11px] tracking-[0.22em] uppercase text-[#7A7A7A] font-semibold">Regular Investment</div>
              <div className="font-display text-2xl text-[#7A7A7A] line-through opacity-70">$10,000</div>
              <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-[#F7931E] font-semibold">Limited-Time Enrollment</div>
              <div className="font-display text-4xl text-[#F7931E] font-semibold">$5,000</div>
              <p className="mt-4 text-sm text-[#C7CFDD] leading-relaxed">
                Take advantage of the current limited-time enrollment offer to secure your seat in the Bounce City University mentorship for $5,000.
              </p>
              <div className="mt-auto pt-6">
                <a
                  data-testid="pay-full-btn"
                  href={FULL_PAYMENT_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onFullPayClick}
                  className="bcu-btn-primary w-full justify-center"
                >
                  Enroll Now — $5,000 <ExternalLink className="h-4 w-4" />
                </a>
                <p className="mt-3 text-xs text-[#7A8296] leading-relaxed">
                  Your seat is confirmed after payment is successfully completed and received, subject to availability.
                </p>
              </div>
            </div>

            {/* Option 2 — Financing Waitlist (deposit option hidden during current $5,000 offer window) */}
            <div data-testid="secure-option-financing" className="bcu-card p-7 flex flex-col">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#78C800] font-bold">Option 02</div>
              <h3 className="mt-3 font-display text-2xl leading-tight">Join the Financing Options Waitlist</h3>
              <p className="mt-4 text-sm text-[#C7CFDD] leading-relaxed">
                Do not currently have access to the full program investment or deposit? Join the waitlist to be notified if financing or alternative payment options become available.
              </p>
              <div className="mt-auto pt-6">
                <button
                  data-testid="financing-waitlist-btn"
                  type="button"
                  onClick={joinFinancingWaitlist}
                  disabled={financingBusy || financingJoined}
                  aria-live="polite"
                  className="bcu-btn-primary w-full justify-center disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {financingJoined ? (
                    <>Added to Waitlist <Check className="h-4 w-4" /></>
                  ) : financingBusy ? (
                    <>Saving… <Loader2 className="h-4 w-4 animate-spin" /></>
                  ) : (
                    <>Join the Financing Waitlist <ArrowRight className="h-4 w-4" /></>
                  )}
                </button>
                {financingJoined && (
                  <p data-testid="financing-waitlist-success" className="mt-3 text-xs text-[#78C800] leading-relaxed">
                    You've been added to the financing options waitlist. Our team will contact you if suitable financing or alternative payment arrangements become available.
                  </p>
                )}
                {!financingJoined && financingError && (
                  <p data-testid="financing-waitlist-error" role="alert" className="mt-3 text-xs text-[#F7931E] leading-relaxed">
                    {financingError}
                  </p>
                )}
                {!financingJoined && !financingError && (
                  <p className="mt-3 text-xs text-[#7A8296] leading-relaxed">
                    Financing options are not guaranteed.
                  </p>
                )}
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-[#7A8296] leading-relaxed max-w-3xl">
            Payments are processed securely by Square on an external checkout page. Bounce City University does not collect or store your payment-card information on this website. Financing options are not guaranteed and may be subject to third-party approval and separate terms.
          </p>
        </section>

        <div className="mt-14 flex flex-wrap gap-4">
          <Link to="/" data-testid="return-home-btn" className="bcu-btn-primary">Return to home <ArrowRight className="h-4 w-4" /></Link>
          <Link to="/blueprint" data-testid="review-blueprint-btn" className="bcu-btn-secondary">Review the 90-Day Blueprint</Link>
        </div>
      </div>
    </div>
  );
}
